import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../../services/api';
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiSave, FiFileText, FiHeadphones, FiBook, FiEdit3 } from 'react-icons/fi';

const TestBlocksCheckingDetailPage = () => {
    const { resultId } = useParams();
    const navigate = useNavigate();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [writingScores, setWritingScores] = useState({});
    const [writingFeedback, setWritingFeedback] = useState({});

    useEffect(() => {
        loadResult();
    }, [resultId]);

    const loadResult = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getTestBlocksResultDetail(resultId);
            const data = response.data || response; // Axios возвращает { data: {...} }
            console.log('Result data:', data); // Отладка
            setResult(data);
            
            // Инициализируем оценки для письменных блоков
            const scores = {};
            const feedback = {};
            if (data.blocks && Array.isArray(data.blocks)) {
                data.blocks.forEach(block => {
                    if (block.block_type === 'writing' && block.result) {
                        scores[block.id] = block.result.score || 0;
                        feedback[block.id] = block.result.writing_feedback || '';
                    }
                });
            }
            setWritingScores(scores);
            setWritingFeedback(feedback);
        } catch (error) {
            console.error('Ошибка загрузки результата:', error);
            alert('Ошибка при загрузке результата теста');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            
            // Подготавливаем данные для сохранения
            const writingResults = Object.keys(writingScores).map(blockId => ({
                blockId: parseInt(blockId),
                score: writingScores[blockId],
                feedback: writingFeedback[blockId]
            }));

            await adminAPI.updateTestBlocksWritingScores(resultId, { writingResults });
            alert('Оценки успешно сохранены!');
            navigate('/admin/checking/test-blocks');
        } catch (error) {
            console.error('Ошибка сохранения:', error);
            alert('Ошибка при сохранении оценок');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const formatAnswer = (answer) => {
        if (answer === null || answer === undefined) return 'Нет ответа';
        if (typeof answer === 'string') {
            try {
                const parsed = JSON.parse(answer);
                if (Array.isArray(parsed)) {
                    return parsed.join(', ');
                }
                return answer;
            } catch {
                return answer;
            }
        }
        if (Array.isArray(answer)) {
            return answer.join(', ');
        }
        return String(answer);
    };

    const renderBlockResult = (block) => {
        if (block.block_type === 'grammar') {
            return (
                <div key={block.id} style={{ marginBottom: '30px' }}>
                    <h3><FiFileText style={{ marginRight: '8px' }} />{block.title}</h3>
                    <div style={{ 
                        padding: '15px', 
                        background: '#f9f9f9', 
                        borderRadius: '8px',
                        marginBottom: '15px'
                    }}>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#4a90e2' }}>
                            Результат: {block.result?.score || 0}/{block.result?.max_score || 0} баллов
                        </div>
                    </div>
                    {block.result?.exercises?.map((exercise, idx) => (
                        <div key={idx} style={{ marginBottom: '20px' }}>
                            {exercise.questions.map((q, qIdx) => (
                                <div key={qIdx} style={{
                                    padding: '12px',
                                    background: q.is_correct ? '#f6ffed' : '#fff2f0',
                                    border: `1px solid ${q.is_correct ? '#b7eb8f' : '#ffccc7'}`,
                                    borderRadius: '6px',
                                    marginBottom: '10px'
                                }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <strong>Вопрос {qIdx + 1}:</strong>
                                    </div>
                                    <div style={{ marginBottom: '8px', color: '#333' }}>
                                        {q.question_text}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666' }}>
                                        Ответ студента: <strong>{formatAnswer(q.user_answer)}</strong>
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666' }}>
                                        Правильный ответ: <strong>{formatAnswer(q.correct_answer)}</strong>
                                    </div>
                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {q.is_correct ? (
                                            <><FiCheckCircle color="#52c41a" /> <span style={{ color: '#52c41a' }}>Правильно</span></>
                                        ) : (
                                            <><FiXCircle color="#ff4d4f" /> <span style={{ color: '#ff4d4f' }}>Неправильно</span></>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            );
        } else if (block.block_type === 'listening') {
            return (
                <div key={block.id} style={{ marginBottom: '30px' }}>
                    <h3><FiHeadphones style={{ marginRight: '8px' }} />{block.title}</h3>
                    <div style={{ 
                        padding: '15px', 
                        background: '#f9f9f9', 
                        borderRadius: '8px',
                        marginBottom: '15px'
                    }}>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#4a90e2' }}>
                            Результат: {block.result?.score || 0}/{block.result?.max_score || 0} баллов
                        </div>
                    </div>
                    {block.result?.questions?.map((q, idx) => (
                        <div key={idx} style={{
                            padding: '12px',
                            background: q.is_correct ? '#f6ffed' : '#fff2f0',
                            border: `1px solid ${q.is_correct ? '#b7eb8f' : '#ffccc7'}`,
                            borderRadius: '6px',
                            marginBottom: '10px'
                        }}>
                            <div style={{ marginBottom: '8px' }}>
                                <strong>Вопрос {idx + 1}:</strong>
                            </div>
                            <div style={{ marginBottom: '8px', color: '#333' }}>
                                {q.question_text}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                Ответ студента: <strong>{formatAnswer(q.user_answer)}</strong>
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                Правильный ответ: <strong>{formatAnswer(q.correct_answer)}</strong>
                            </div>
                            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {q.is_correct ? (
                                    <><FiCheckCircle color="#52c41a" /> <span style={{ color: '#52c41a' }}>Правильно</span></>
                                ) : (
                                    <><FiXCircle color="#ff4d4f" /> <span style={{ color: '#ff4d4f' }}>Неправильно</span></>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        } else if (block.block_type === 'reading') {
            return (
                <div key={block.id} style={{ marginBottom: '30px' }}>
                    <h3><FiBook style={{ marginRight: '8px' }} />{block.title}</h3>
                    <div style={{ 
                        padding: '15px', 
                        background: '#f9f9f9', 
                        borderRadius: '8px',
                        marginBottom: '15px'
                    }}>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#4a90e2' }}>
                            Результат: {block.result?.score || 0}/{block.result?.max_score || 0} баллов
                        </div>
                    </div>
                    {block.result?.questions?.map((q, idx) => (
                        <div key={idx} style={{
                            padding: '12px',
                            background: q.is_correct ? '#f6ffed' : '#fff2f0',
                            border: `1px solid ${q.is_correct ? '#b7eb8f' : '#ffccc7'}`,
                            borderRadius: '6px',
                            marginBottom: '10px'
                        }}>
                            <div style={{ marginBottom: '8px' }}>
                                <strong>Вопрос {idx + 1}:</strong>
                            </div>
                            <div style={{ marginBottom: '8px', color: '#333' }}>
                                {q.question_text}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                Ответ студента: <strong>{formatAnswer(q.user_answer)}</strong>
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                Правильный ответ: <strong>{formatAnswer(q.correct_answer)}</strong>
                            </div>
                            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {q.is_correct ? (
                                    <><FiCheckCircle color="#52c41a" /> <span style={{ color: '#52c41a' }}>Правильно</span></>
                                ) : (
                                    <><FiXCircle color="#ff4d4f" /> <span style={{ color: '#ff4d4f' }}>Неправильно</span></>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        } else if (block.block_type === 'writing') {
            const maxScore = block.writingBlock?.max_points || 10;
            return (
                <div key={block.id} style={{ marginBottom: '30px' }}>
                    <h3><FiEdit3 style={{ marginRight: '8px' }} />{block.title}</h3>
                    
                    <div style={{
                        padding: '15px',
                        background: '#f0f5ff',
                        borderRadius: '8px',
                        marginBottom: '15px',
                        border: '1px solid #d6e4ff'
                    }}>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Задание:</strong>
                            <div style={{ marginTop: '5px', color: '#666' }}>
                                {block.writingBlock?.task_description}
                            </div>
                        </div>
                        <div style={{ fontSize: '14px', color: '#999' }}>
                            Требования: {block.writingBlock?.min_words}-{block.writingBlock?.max_words} слов
                        </div>
                    </div>
                    
                    <div style={{
                        padding: '15px',
                        background: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        marginBottom: '15px'
                    }}>
                        <strong>Ответ студента:</strong>
                        <div style={{ marginTop: '10px', lineHeight: '1.6' }}>
                            {block.result?.writing_text || 'Нет ответа'}
                        </div>
                        {block.result?.writing_text && (
                            <div style={{ marginTop: '10px', fontSize: '14px', color: '#999' }}>
                                Количество слов: {block.result.writing_text.split(/\s+/).filter(w => w).length}
                            </div>
                        )}
                    </div>
                    
                    <div style={{
                        padding: '15px',
                        background: '#fff7e6',
                        borderRadius: '8px',
                        border: '1px solid #ffd591'
                    }}>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                Оценка (макс. {maxScore} баллов)
                            </label>
                            <input
                                type="number"
                                min="0"
                                max={maxScore}
                                value={writingScores[block.id] || 0}
                                onChange={(e) => setWritingScores({
                                    ...writingScores,
                                    [block.id]: Math.min(maxScore, Math.max(0, parseInt(e.target.value) || 0))
                                })}
                                style={{
                                    width: '150px',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    fontSize: '16px'
                                }}
                                disabled={result.writing_checked}
                            />
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                Комментарий преподавателя
                            </label>
                            <textarea
                                value={writingFeedback[block.id] || ''}
                                onChange={(e) => setWritingFeedback({
                                    ...writingFeedback,
                                    [block.id]: e.target.value
                                })}
                                rows={4}
                                placeholder="Напишите комментарий к работе студента..."
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    resize: 'vertical'
                                }}
                                disabled={result.writing_checked}
                            />
                        </div>
                    </div>
                </div>
            );
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                Загрузка...
            </div>
        );
    }

    if (!result) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                Результат не найден
            </div>
        );
    }

    const autoScore = result.score || 0;
    const totalMaxScore = result.max_score || 0; // Уже включает все блоки, включая письмо
    
    // Вычисляем максимальный балл за письмо
    const writingMaxScores = (result.blocks || [])
        .filter(b => b.block_type === 'writing')
        .reduce((sum, b) => sum + (b.writingBlock?.max_points || 0), 0);
    
    // Автоматический максимальный балл = общий - письмо
    const autoMaxScore = totalMaxScore - writingMaxScores;
    
    const currentWritingScore = Object.values(writingScores).reduce((sum, s) => sum + s, 0);
    const finalScore = result.writing_checked ? result.final_score : (autoScore + currentWritingScore);
    const finalPercentage = totalMaxScore > 0 ? Math.round((finalScore / totalMaxScore) * 100) : 0;

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button
                    onClick={() => navigate('/admin/checking/test-blocks')}
                    style={{
                        padding: '10px 20px',
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <FiArrowLeft /> Назад к списку
                </button>
                
                <button
                    onClick={async () => {
                        if (window.confirm('Назначить этот тест студенту для повторного прохождения?')) {
                            try {
                                await adminAPI.reopenTest(resultId);
                                alert('Тест назначен для повторного прохождения');
                                navigate('/admin/checking/test-blocks');
                            } catch (error) {
                                console.error('Ошибка:', error);
                                alert('Ошибка при назначении теста');
                            }
                        }
                    }}
                    style={{
                        padding: '10px 20px',
                        background: '#fa8c16',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontWeight: '500'
                    }}
                >
                    <FiArrowLeft style={{ transform: 'rotate(180deg)' }} /> Назначить повторно
                </button>
            </div>

            <div style={{
                padding: '25px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                marginBottom: '30px'
            }}>
                <h2 style={{ marginTop: 0 }}>{result.test_title}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                        <div style={{ color: '#999', fontSize: '14px' }}>Студент</div>
                        <div style={{ fontSize: '18px', fontWeight: '600' }}>{result.student_name}</div>
                    </div>
                    <div>
                        <div style={{ color: '#999', fontSize: '14px' }}>Дата сдачи</div>
                        <div style={{ fontSize: '18px', fontWeight: '600' }}>{formatDate(result.completed_at)}</div>
                    </div>
                </div>

                <div style={{
                    padding: '20px',
                    background: '#f0f5ff',
                    borderRadius: '8px',
                    border: '2px solid #4a90e2'
                }}>
                    <div style={{ marginBottom: '15px' }}>
                        <div style={{ fontSize: '16px', color: '#666' }}>Автоматическая оценка (без письма)</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#4a90e2' }}>
                            {autoScore}/{autoMaxScore} баллов ({autoMaxScore > 0 ? Math.round((autoScore/autoMaxScore)*100) : 0}%)
                        </div>
                    </div>
                    
                    {!result.writing_checked && (
                        <div style={{ marginBottom: '15px' }}>
                            <div style={{ fontSize: '16px', color: '#666' }}>Письменное задание</div>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#faad14' }}>
                                {currentWritingScore}/{writingMaxScores} баллов
                            </div>
                        </div>
                    )}
                    
                    <div style={{ paddingTop: '15px', borderTop: '1px solid #d6e4ff' }}>
                        <div style={{ fontSize: '16px', color: '#666' }}>
                            {result.writing_checked ? 'Итоговая оценка' : 'Предварительная итоговая оценка'}
                        </div>
                        <div style={{ fontSize: '28px', fontWeight: '700', color: '#52c41a' }}>
                            {finalScore}/{totalMaxScore} баллов ({finalPercentage}%)
                        </div>
                    </div>
                </div>
            </div>

            {(result.blocks || []).map(block => renderBlockResult(block))}

            {!result.writing_checked && (
                <div style={{
                    position: 'sticky',
                    bottom: '20px',
                    padding: '20px',
                    background: 'white',
                    border: '2px solid #52c41a',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: '600' }}>
                            Итоговая оценка: {finalScore}/{totalMaxScore} ({finalPercentage}%)
                        </div>
                        <div style={{ fontSize: '14px', color: '#999' }}>
                            Проверьте все письменные задания и нажмите "Сохранить"
                        </div>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                            padding: '12px 32px',
                            background: '#52c41a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: saving ? 0.6 : 1
                        }}
                    >
                        <FiSave /> {saving ? 'Сохранение...' : 'Сохранить оценки'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default TestBlocksCheckingDetailPage;
